import { createContext, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { tokenVars, type Surface } from './tokens';

export type ThemeMode = 'dark' | 'light';
export type ThemeChoice = ThemeMode | 'system';
const KEY = 'zr-theme';

function readChoice(): ThemeChoice {
  try {
    const q = new URLSearchParams(window.location.search).get('theme');
    if (q === 'dark' || q === 'light' || q === 'system') {
      localStorage.setItem(KEY, q);
      return q;
    }
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' ? v : 'system';
  } catch {
    return 'system';
  }
}

const systemMode = (): ThemeMode =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';

type Ctx = { mode: ThemeMode; choice: ThemeChoice; setChoice: (c: ThemeChoice) => void };
const ThemeCtx = createContext<Ctx>({ mode: 'dark', choice: 'system', setChoice: () => {} });

/** Follows the phone’s light/dark setting; `?theme=dark|light|system` (remembered) overrides it — the mockup has no theme switch. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);
  const [system, setSystem] = useState<ThemeMode>(systemMode);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;
    const on = () => setSystem(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const mode = choice === 'system' ? system : choice;
  useEffect(() => {
    document.body.dataset.theme = mode;
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', mode === 'dark' ? '#0D0D0D' : '#FFFFFF'));
  }, [mode]);
  const value = useMemo(
    () => ({
      mode,
      choice,
      setChoice: (c: ThemeChoice) => {
        try {
          localStorage.setItem(KEY, c);
        } catch {
          /* private mode: keep it for this visit */
        }
        setChoiceState(c);
      },
    }),
    [mode, choice],
  );
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);

/** The design tokens for a theme + surface, as CSS variables (--bg, --ink, --ac, --h1 …). */
export function themeVars(mode: ThemeMode, surface: Surface = 'base'): CSSProperties {
  return tokenVars(mode, surface) as CSSProperties;
}
